import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import FluidMask from '@/components/base/fluid-mask'
import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'
import { isAcademy } from '@/constants'

import {
  imageFullMediaRevealAnimation,
  imageFullRevealAnimation
} from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type ImageFullProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    pretitle: string
    title: string
    paragraph: string
    media: MediaElement
    image_fit: 'cover' | 'contain'
    dark_overlay: boolean
    link: WPLink
  }>

const ImageFull = ({
  pretitle,
  title,
  paragraph,
  link,
  className,
  media,
  image_fit,
  dark_overlay,
  ...props
}: ImageFullProps) => {
  const ContentBlock = () => (
    <>
      <Motion animation={imageFullMediaRevealAnimation}>
        {(motionRef) => (
          <div
            ref={motionRef}
            className={cn('absolute left-margin right-margin top-0 h-full', {
              'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-gradient-to-t after:from-[#000000]/80 after:to-[#000000]/0':
                isAcademy && dark_overlay
            })}
          >
            <FluidMask amplitude={15} gradient>
              {(ref) => (
                <Media
                  ref={ref}
                  className={cn([
                    'h-full w-full',
                    {
                      'object-cover': image_fit === 'cover',
                      'object-contain': image_fit === 'contain'
                    }
                  ])}
                  {...media}
                />
              )}
            </FluidMask>
          </div>
        )}
      </Motion>

      <Container className="dark relative">
        <Grid className="aspect-square md:aspect-video">
          <div
            className={cn(
              'col-span-2 md:col-span-3 lg:col-span-4 lg:col-start-2',
              'flex items-end'
            )}
          >
            <Motion animation={imageFullRevealAnimation}>
              {(motionRef) => (
                <div
                  ref={motionRef}
                  className={cn(
                    'grid gap-y-spacing-lg',
                    'px-margin py-spacing-lg lg:px-0 lg:py-spacing-2xl'
                  )}
                >
                  {pretitle ? (
                    <Typography
                      variant="overline"
                      paragraph
                      className="font-bold uppercase text-text-overlay-primary-light dark:text-text-overlay-primary-dark"
                      data-motion-pretitle
                    >
                      {pretitle}
                    </Typography>
                  ) : null}

                  {title ? (
                    <Typography
                      variant="h3"
                      component="h2"
                      className={cn({
                        'text-text-overlay-primary-light dark:text-text-overlay-primary-dark':
                          isAcademy
                      })}
                      data-motion-title
                    >
                      {title}
                    </Typography>
                  ) : null}

                  {paragraph ? (
                    <Typography
                      variant="large"
                      paragraph
                      className={cn({
                        'text-text-overlay-primary-light dark:text-text-overlay-primary-dark':
                          isAcademy
                      })}
                      data-motion-paragraph
                    >
                      {paragraph}
                    </Typography>
                  ) : null}

                  {link && link?.url ? (
                    <div>
                      <Button
                        asChild
                        isLiquid
                        isInverted
                        variant="primary"
                        data-motion-cta
                      >
                        <Link href={link.url} scroll={false}>
                          <TextEffect>
                            <Typography variant="regular">
                              {link.title}
                            </Typography>
                          </TextEffect>
                        </Link>
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </Motion>
          </div>
        </Grid>
      </Container>
    </>
  )

  // Render normale senza link
  return (
    <Section className={cn(className)} {...props}>
      <ContentBlock />
    </Section>
  )
}

export default ImageFull
